import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';

const CurrentAffairsTestPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleStart = () => {
    if (user) navigate('/test-zone');
    else navigate('/login');
  };

  const faqs = [
    { q: 'Is Current Affairs Test free?', a: 'Yes, you can attempt daily current affairs tests for free.' },
    { q: 'How often are questions updated?', a: 'Questions are updated daily with the latest events and news.' },
    { q: 'Will I get results after the test?', a: 'Yes, instant score and performance analysis are provided.' },
    { q: 'Is this useful for CUET & NET exams?', a: 'Yes, current affairs is an important section in CUET, UGC NET, and many other exams.' },
    { q: 'Do I need to register?', a: 'Yes, quick registration is required to track your performance over time.' },
  ];

  return (
    <>
      <Helmet>
        <title>Current Affairs Test Free | Daily GK Quiz for Competitive Exams</title>
        <meta name="description" content="Attempt current affairs test free online. Practice daily GK quiz, improve awareness & boost your exam preparation with updated questions." />
        <meta name="keywords" content="current affairs test, daily GK quiz, current affairs quiz, GK test online, competitive exam current affairs" />
        <link rel="canonical" href="https://besst.in/current-affairs-test" />
        <meta property="og:title" content="Current Affairs Test Free | Daily GK Quiz for Competitive Exams" />
        <meta property="og:description" content="Attempt current affairs test free online. Practice daily GK quiz and boost your exam preparation." />
        <meta property="og:url" content="https://besst.in/current-affairs-test" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BESST" />
      </Helmet>
      <div className="min-h-screen pt-32 pb-20">
        {/* Hero */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="text-center max-w-4xl mx-auto">
            <div className="hero-badge mx-auto mb-6">🌍 Daily GK Practice</div>
            <h1 className="text-4xl lg:text-6xl font-extrabold gradient-text mb-6 leading-tight">
              Current Affairs Test
            </h1>
            <p className="text-xl text-text-muted mb-4">
              Stay ahead with free daily current affairs tests and GK quizzes
            </p>
            <p className="text-text-muted mb-8">✅ Daily Updated Questions &nbsp;•&nbsp; ⚡ Instant Results &nbsp;•&nbsp; 🆓 100% Free</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/current-affairs-quiz"
                className="btn-primary inline-flex items-center gap-3 px-8 py-4 text-lg font-bold rounded-full"
              >
                <span>🧪</span>
                <span>Free Current Affairs Test</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              <Link to="/register" className="px-8 py-4 text-lg font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">
                Register Free
              </Link>
            </div>
            <p className="text-xs text-accent-gold mt-4">🔄 New questions updated daily</p>
          </div>
        </div>

        {/* Quick Start */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-3xl font-bold gradient-text text-center mb-10">Start Your Test Instantly</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { icon: '🌍', label: 'Daily Current Affairs Quiz', desc: 'Latest events & news' },
              { icon: '🧠', label: 'General Knowledge Test', desc: 'Comprehensive GK coverage' },
              { icon: '🎯', label: 'Mixed Practice Sets', desc: 'Multi-topic practice' },
            ].map(t => (
              <button key={t.label} onClick={handleStart} className="glass-card text-center hover:border-primary/50 transition-colors">
                <div className="text-4xl mb-3">{t.icon}</div>
                <h3 className="text-text-light font-bold mb-1">{t.label}</h3>
                <p className="text-text-muted text-sm">{t.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Why take the test */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-text-light mb-4">📊 Why Take Current Affairs Test?</h2>
            <p className="text-text-muted mb-4 text-sm">Current affairs play a crucial role in competitive exams like CUET UG & PG, UGC NET/JRF, and other entrance exams.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {['Improve awareness', 'Boost accuracy', 'Stay updated with latest events', 'Score better in exams'].map(b => (
                <div key={b} className="flex items-center gap-3 p-3 bg-bg-secondary rounded-xl">
                  <span className="text-green-400">✔</span>
                  <span className="text-text-light text-sm">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-3xl font-bold gradient-text text-center mb-10">Everything You Need to Stay Ahead</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              { icon: '📅', label: 'Daily Updated Questions' },
              { icon: '⚡', label: 'Instant Results' },
              { icon: '⏱️', label: 'Time-Based Quizzes' },
              { icon: '📊', label: 'Performance Tracking' },
              { icon: '🎯', label: 'Exam-Level Questions' },
              { icon: '📱', label: 'Easy-to-Use Interface' },
            ].map(f => (
              <div key={f.label} className="glass-card text-center p-4">
                <div className="text-3xl mb-2">{f.icon}</div>
                <div className="text-text-light text-sm font-semibold">{f.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* How daily practice helps */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">Build a Strong Habit & Improve Performance</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {['Retain information better', 'Improve speed & accuracy', 'Build consistency', 'Stay exam-ready'].map((b, i) => (
              <div key={b} className="glass-card text-center p-4">
                <div className="text-2xl font-bold text-primary mb-2">0{i + 1}</div>
                <div className="text-text-muted text-sm">{b}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Who is it for */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-text-light mb-6">🎯 Ideal for All Competitive Exam Aspirants</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {['CUET UG Aspirants', 'CUET PG Aspirants', 'UGC NET Aspirants', 'Banking & SSC Students', 'State Exam Candidates'].map(t => (
                <span key={t} className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-semibold">{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Cross-sell */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-6">Boost Your Preparation Further</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { label: 'Mock Tests', href: '/test-zone', icon: '🧪' },
              { label: 'Study Materials', href: '/study-materials', icon: '📚' },
              { label: 'PYQs', href: '/pyq-cuet', icon: '📄' },
              { label: 'Self-Paced Courses', href: '/self-paced', icon: '🎥' },
            ].map(l => (
              <Link key={l.label} to={l.href} className="glass-card text-center hover:border-primary/50 transition-colors p-4">
                <div className="text-3xl mb-2">{l.icon}</div>
                <div className="text-text-light text-sm font-semibold">{l.label}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="glass-card">
                <summary className="font-semibold text-text-light cursor-pointer list-none flex justify-between items-center">
                  {f.q} <span className="text-primary text-xl">+</span>
                </summary>
                <p className="text-text-muted mt-3 text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="glass-card text-center max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-text-light mb-3">Practice Daily & Stay Ahead</h2>
            <p className="text-text-muted mb-2">New questions added every day — don't miss out.</p>
            <p className="text-accent-gold text-sm mb-6">🔄 Questions updated daily</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleStart} className="btn-primary px-8 py-3 font-bold rounded-xl">🚀 Start Today's Test</button>
              <Link to="/register" className="px-8 py-3 font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">
                Register Free
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CurrentAffairsTestPage;
